import { Flex, Form, Select } from 'antd';
import { useSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useFilterOptions } from 'business/quality-control/pages/segment-history/hooks/use-filter-options';
import { SegmentHistoryTableFilterData } from 'business/quality-control/pages/segment-history/mobile';
import {
  defaultFilters,
  useResetSegmentHistoryFilters,
  useSegmentHistoryFilters,
  useSegmentHistoryFiltersFormName,
} from 'business/quality-control/pages/segment-history/providers/filters';
import {
  Module_Enum,
  useGetCurrentRingQuery,
  useGetFiltersDataQuery,
} from 'generated/graphql';
import { FilterData } from 'technical/filter-data';
import Button from 'ui/button';
import { SelectedItemsFooter } from 'ui/filter-footer';
import { NumberRangeInputWithMessage } from 'ui/form/number-range-input-with-message';

import styles from './index.module.scss';

export const MobileSegmentHistoryFilters = ({
  filterData,
}: {
  filterData: FilterData<SegmentHistoryTableFilterData>;
}) => {
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const { t } = useTranslation();
  const [form] = Form.useForm();
  const formName = useSegmentHistoryFiltersFormName();
  const filters = useSegmentHistoryFilters();
  const resetFilters = useResetSegmentHistoryFilters();

  const [, setSearchParams] = useSearchParams();

  const headers = useGraphqlApiHeaders(Module_Enum.QualityScan);
  const { data, loading } = useGetFiltersDataQuery({
    variables: {
      constructionSiteId: currentConstructionSite.id,
      projectId: currentConstructionSite.projectId,
    },
    context: {
      headers,
    },
  });

  const { data: currentRingData, error: currentRingError } =
    useGetCurrentRingQuery({
      variables: { constructionSiteId: currentConstructionSite.id },
      context: {
        headers,
      },
    });

  const {
    siteOptions,
    lastScanRangeOptions,
    serialNumberOptions,
    setFirstSerialNumberOption,
  } = useFilterOptions(data);

  return (
    <>
      <Form layout="inline" form={form} name={formName} initialValues={filters}>
        <Form.Item
          name="serialNumber"
          label={t('input.serialNumber.label')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.serialNumber.placeholder')}
            showSearch
            allowClear
            onSearch={(value) => {
              setFirstSerialNumberOption(value);
            }}
            onClear={() => {
              setFirstSerialNumberOption(null);
              setSearchParams({});
            }}
            options={serialNumberOptions}
            loading={loading}
            className={styles.segmentDetailsSelect}
          />
        </Form.Item>

        <Form.Item
          name="site"
          label={t('input.scanSites.selectLabel')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.scanSites.placeholder')}
            options={siteOptions}
            className={styles.segmentDetailsSelect}
            allowClear
          />
        </Form.Item>

        <NumberRangeInputWithMessage
          name="ringNumberRange"
          label={t('input.ringIntervalRange.selectLabel')}
          leftPlaceholder={t('input.ringRange.startPlaceholder')}
          rightPlaceholder={t('input.ringRange.endPlaceholder')}
          message={
            currentRingError
              ? t('dataAnalysis.errors.currentRingNumberError')
              : t('dataAnalysis.labels.currentRingNumber', {
                  ringNumber:
                    currentRingData?.parameter_ring_aggregate.aggregate?.max
                      ?.ringNumber,
                })
          }
        />

        <Form.Item
          name="lastScanRange"
          label={t('input.lastScanRange.selectLabel')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.lastScanRange.placeholder')}
            options={lastScanRangeOptions}
            className={styles.rangeSelect}
            allowClear
          />
        </Form.Item>
      </Form>
      <Flex justify="center">
        <Button
          onClick={() => {
            form.setFieldsValue(defaultFilters);
            setFirstSerialNumberOption(null);
            setSearchParams({});
            resetFilters();
          }}
        >
          {t('common.clearFilter')}
        </Button>
      </Flex>
      {filterData?.selectedSegments ? (
        <SelectedItemsFooter mobile>
          {t('productionAndPerformance.ring.selectedRings', {
            selectedRings: filterData?.selectedSegments,
          })}
        </SelectedItemsFooter>
      ) : null}
    </>
  );
};
